import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs, { Dayjs } from 'dayjs';
import { SelectChangeEvent } from '@mui/material';

import { dateNormalizer, errorNormalizer } from '../../../shared/Helpers/functions';
import { toClientDateTime } from '../../../shared/Helpers/timezones';
import { CONFIRM_ERROR_MESSAGE_ASSESMENT, SessionType as SessionTypeEnum,  } from '../../../shared/Helpers/constants';
import { CustomAlert, CustomButton, CustomSelect, Picker, Text } from '../../../shared/uiComponents';
import { useCompareDates } from '../../../shared/Helpers/hooks';
import { TimeInputsWrapper } from '../../Reports/helpers';
import AdminTools from '../CalendarHeader/adminTools';
import { SelectDataProperties } from '../../../shared/uiComponents/Dropdown/searchSelect';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { setAddScheduledSession, setAdminAddEvent } from '../../../redux/State/clientSlice/calendarHelperSlice';
import { AdminCreateEventProperties, EventDataProperties, isDiscretionaryEvent } from '../../../redux/API/ClientAPIHelpers/calendarProperties';
import { adminCreateEvent } from '../../../redux/State/clientSlice/calendarSlice';
import { SessionType } from '../../../shared/Assets/sessionDetailsSVGs';
import { getSessionTypes } from '../../../redux/State/clientSlice/sessionSlice';
import {
	clearDiscretionarySessionState,
	getUserRemainingHours,
} from '../../../redux/State/userSlice/userSessionAllocationSlice';

const todayDayjs = dayjs();
const initialExpirationDate = todayDayjs.add(2, 'days');

const AddContentAdmin = () => {
	const dispatch = useDispatch<DispatchProperties>();

	const [loading, setLoading] = useState<boolean>(false);
	const [sessionTypeId, setSessionTypeId] = useState<number>(0);

	const [dateValue, setDateValue] = useState<Dayjs | null>(todayDayjs);
	const [startTimeValue, setStartTimeValue] = useState<Dayjs | null>(null);
	const [endTimeValue, setEndTimeValue] = useState<Dayjs | null>(null);
	const [expirationDateValue, setExpirationDateValue] = useState<Dayjs | null>(initialExpirationDate);
	const [confirmOverrideOpen, setConfirmOverrideOpen] = useState<boolean>(false);

	// Local selection so the modal never mutates the global calendar filter.
	const [provider, setProvider] = useState<SelectDataProperties | null>(null);
	const [client, setClient] = useState<SelectDataProperties | null>(null);
	const [searchProvider, setSearchProvider] = useState<string>('');
	const [searchClient, setSearchClient] = useState<string>('');

	const sessionTypes = useSelector((state) => state.session.sessionTypes);
	const days = useSelector((state) => state.calendarHelper.displayedCalendarPageDays);
	const selectedDay = useSelector((state) => state.calendarHelper.selectedDay);
	const userSessionAllocation = useSelector((state) => state.userSessionAllocation.userRemainingHours);

	const calendarEvents = useSelector((state) => state.calendar.events);
	const userClients = useSelector((state) => state.userClient.userClients);
	const userId = provider?.id;
	const clientId = client?.id;
	const clientTimeZoneId = userClients?.query?.find((c) => c.id === clientId)?.timeZoneId;

	const { hasError, message } = useCompareDates(startTimeValue, endTimeValue);

	const timeOffConflict = useMemo(() => {
		if (!userId || !dateValue || !calendarEvents) return false;
    const selectedDate = dateValue.format('YYYY-MM-DD');
    
		return calendarEvents.some(
			(e) =>
				!isDiscretionaryEvent(e) &&
				(e as EventDataProperties).isRecurringScheduleSession &&
				(e as EventDataProperties).isCancelledApproved &&
				(e as EventDataProperties).user.id === userId &&
				dayjs(e.date).format('YYYY-MM-DD') === selectedDate,
		);
	}, [userId, dateValue, calendarEvents]);

	useEffect(() => {
		const { day, month, year } = selectedDay;
		const selectedDateValue = new Date(year, month, day);
		if (!clientId || !userId) return;

		dispatch(
			getSessionTypes({
				clientId,
				date: dateNormalizer(dayjs(selectedDateValue)),
				userId,
			}),
		);
	}, [dispatch, clientId, userId, selectedDay]);

	const onStartTimeChange = (value: Dayjs | null) => {
		setStartTimeValue(value);

		if (value && (!endTimeValue || (startTimeValue && endTimeValue.isSame(startTimeValue.add(1, 'h'))))) {
			setEndTimeValue(value.add(1, 'h'));
		}
	};

	useEffect(() => {
		const selectedDate = dayjs()
			.set('year', selectedDay.year)
			.set('month', selectedDay.month)
			.set('date', selectedDay.day);
		setDateValue(selectedDate);
	}, [selectedDay]);

	useEffect(() => {

		const selectedType = sessionTypes.find((st) => st.id === sessionTypeId);
		if (!sessionTypeId || selectedType?.type !== SessionTypeEnum.DiscretionarySession || !userId || !dateValue) return;
		const date = dateNormalizer(dateValue);
		dispatch(getUserRemainingHours({ userId, date, sessionType: sessionTypeId }));
	}, [dispatch, sessionTypeId, userId, dateValue]);

	const onTypeChange = (event: SelectChangeEvent<string>) => {
		const value = event.target.value;
		setSessionTypeId(parseInt(value));
	};

	const buildPayload = (force = false): AdminCreateEventProperties | null => {
		if (!startTimeValue || !endTimeValue || !days.length || !dateValue || !userId || !clientId || !expirationDateValue)
			return null;

		const startDate = dateValue.set('hour', startTimeValue.hour()).set('minute', startTimeValue.minute()).toDate();
		const endDate = dateValue.set('hour', endTimeValue.hour()).set('minute', endTimeValue.minute()).toDate();

		const startTime = toClientDateTime(startDate, clientTimeZoneId);
		const endTime = toClientDateTime(endDate, clientTimeZoneId);

		const data: AdminCreateEventProperties = {
			userId,
			clientId,
			date: startTime.split('T')[0],
			startTime,
			endTime,
			sessionTypeId,
			expirationDate: dateNormalizer(expirationDateValue),
		};

		return force ? { ...data, isAdminForceOperation: true } : data;
	};

	const submitPayload = (payload: AdminCreateEventProperties) => {
		setLoading(true);
		dispatch(adminCreateEvent(payload))
			.then(unwrapResult)
			.then(() => {
				toast('Created');
				dispatch(setAdminAddEvent(false));
				dispatch(setAddScheduledSession(false));
				dispatch(clearDiscretionarySessionState());
			})
			.catch((e) => {
				const responseMessage = e?.title || (typeof e === 'string' ? e : '');

				if (
					!payload.isAdminForceOperation &&
					responseMessage &&
					responseMessage.includes(CONFIRM_ERROR_MESSAGE_ASSESMENT)
				) {
					setConfirmOverrideOpen(true);
				} else {
					errorNormalizer(e);
				}
			})
			.finally(() => setLoading(false));
	};

	const addSession = () => {
		const payload = buildPayload(false);
		if (!payload) return;
		submitPayload(payload);
	};

	const isCreating = useSelector((state) => state.calendar.loading);

	return (
		<>
			<CustomAlert
				isBusy={isCreating}
				open={confirmOverrideOpen}
				title="Confirm override"
				onClose={() => setConfirmOverrideOpen(false)}
				Content={() => (
					<div>
						<Text title={CONFIRM_ERROR_MESSAGE_ASSESMENT} className="marginBottom16" />
						<div className="confirmOverrideErrorMessageAlertActions">
							<CustomButton title="Cancel" buttonType="secondaryGray" onClick={() => setConfirmOverrideOpen(false)} />
							<CustomButton
								title="Override and submit"
								onClick={() => {
									setConfirmOverrideOpen(false);
									const forced = buildPayload(true);
									if (forced) submitPayload(forced);
								}}
							/>
						</div>
					</div>
				)}
			/>
			<AdminTools
				controlled={{
					provider,
					client,
					searchProvider,
					searchClient,
					setProvider,
					setClient,
					setSearchProvider,
					setSearchClient,
				}}
			/>
			<CustomSelect
				label="Session type"
				data={sessionTypes}
				value={`${sessionTypeId}`}
				setValue={onTypeChange}
				className="marginBottom16"
				startAdornment={<SessionType />}
				disabled={!clientId || !userId}
				description={
					userSessionAllocation
						? `${userSessionAllocation.remainingHoursThisMonth} hours remaining out of ${userSessionAllocation.allocatedHoursPerMonth}.`
						: ''
				}
			/>
			<Picker.CustomDate label="Date of session" onChange={setDateValue} value={dateValue} className="marginBottom16" />
		{timeOffConflict && (
			<div style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '4px', padding: '8px 12px', marginBottom: '16px' }}>
				<Text
					title="⚠️ This provider has approved time off on this date. The session will be auto-cancelled."
					type="pxs"
					fontWeight="medium"
				/>
			</div>
		)}
			<TimeInputsWrapper>
				<Picker.CustomTime label="Start time" value={startTimeValue} onChange={onStartTimeChange} />
				<Picker.CustomTime
					label="End time"
					value={endTimeValue}
					onChange={setEndTimeValue}
					error={hasError}
					errorMessage={message}
				/>
			</TimeInputsWrapper>
			<TimeInputsWrapper>
				<Picker.CustomDate label="Expiration date" value={expirationDateValue} onChange={setExpirationDateValue} />
			</TimeInputsWrapper>
			<CustomButton
				onClick={addSession}
				title="Add calendar event"
				loading={loading}
				disabled={!startTimeValue || !endTimeValue || !clientId || !userId || hasError || sessionTypeId === 0}
			/>
		</>
	);
};

export default AddContentAdmin;
